/**
 * RuleConsequence — the "→ then …" clause: what happens to a matched element.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The consequence used to be a dropdown wedged between five other controls, so
 * the most important thing a rule does was the easiest thing to miss. Here it
 * closes the sentence, on its own tinted line, with the plain-words note
 * (swap / removes-without-replacing / no action yet) directly beneath it.
 */

import { FC, ReactNode } from 'react';
import { CornerDownRight } from 'lucide-react';
import type { FlowLayer, RuleAction, RuleWeightConfig } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import RuleActionsSelect from '../RuleActionsSelect';
import RulePairNote from '../RulePairNote';
import { RuleWeightControls, type WeightIssue } from './weight';

export interface RuleConsequenceProps {
  actions: RuleAction[];
  appliesTo: ReadonlyArray<FlowLayer>;
  onChange: (actions: RuleAction[]) => void;
  /** Payload + handlers for the `'weight'` action's numbers. */
  weight?: RuleWeightConfig;
  onWeightChange?: (next: RuleWeightConfig) => void;
  weightIssue?: WeightIssue;
}

/** The outcome tints its own block, so a destructive rule looks destructive
 *  before anything is read. Unset is warning — it is the state to fix. */
function edge(actions: RuleAction[]): string {
  if (actions.length === 0) return 'border-l-warning bg-warning/5';
  if (actions.includes('eliminate')) return 'border-l-destructive bg-destructive/5';
  if (actions.includes('add-element')) return 'border-l-success bg-success/5';
  return 'border-l-primary bg-primary/5';
}

/** The clause's frame — the tinted block, "↳ then" and the picker — shared
 *  with the Automation's rule cards (its own one-action picker inside). */
export const RuleConsequenceFrame: FC<{ edgeClass: string; picker: ReactNode; children?: ReactNode }> = ({
  edgeClass,
  picker,
  children,
}) => (
  <div className={cn('space-y-1.5 rounded-md border border-border/50 border-l-4 px-2.5 py-2', edgeClass)}>
    <div className="flex flex-wrap items-center gap-2.5">
      <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="text-[13px] font-semibold text-foreground">then</span>
      {picker}
    </div>
    {children}
  </div>
);

export const RuleConsequence: FC<RuleConsequenceProps> = ({
  actions,
  appliesTo,
  onChange,
  weight,
  onWeightChange,
  weightIssue,
}) => (
  <RuleConsequenceFrame
    edgeClass={edge(actions)}
    picker={<RuleActionsSelect value={actions} appliesTo={appliesTo} onChange={onChange} />}
  >
    {actions.includes('weight') && weight && onWeightChange ? (
      <RuleWeightControls
        weight={weight}
        appliesTo={appliesTo}
        onChange={onWeightChange}
        issue={weightIssue}
      />
    ) : null}
    <RulePairNote actions={actions} appliesTo={appliesTo} />
  </RuleConsequenceFrame>
);

export default RuleConsequence;
